import { useTranslation } from '../../i18n/LanguageProvider';
import { institutions, programs, blocksByYear, blockPrograms, blockInstitution, usedPrograms, distinctCourses } from './teachingData';

function blockDescription(block, text) {
  return block.span === 1
    ? text(`Primeiro semestre de ${block.year}`, `First semester of ${block.year}`)
    : text(`Segundo semestre de ${block.year}`, `Second semester of ${block.year}`);
}

function ProgramLink({ program, text }) {
  return (
    <a href={program.url} target="_blank" rel="noreferrer" title={text(program.pt, program.en)}>
      {text(program.short.pt, program.short.en)}
    </a>
  );
}

function TeachingBlock({ block, text }) {
  const institution = institutions[blockInstitution(block)];
  const shared = blockPrograms(block).length === 1 ? programs[block.courses[0].program] : null;

  return (
    <article className="teaching-block" data-institution={blockInstitution(block)} data-span={block.span}>
      <h3 className="teaching-block-heading">
        <span className="teaching-block-label" title={blockDescription(block, text)}>{`${block.year}/${block.span}`}</span>
        <span className="teaching-block-where">
          <a href={institution.url} target="_blank" rel="noreferrer" title={text(institution.pt, institution.en)}>
            {institution.acronym}
          </a>
          {shared && <ProgramLink program={shared} text={text} />}
        </span>
      </h3>
      <ul className="teaching-courses">
        {block.courses.map(item => (
          <li key={item.en}>
            <span className="teaching-course-name">{text(item.pt, item.en)}</span>
            {(!shared || item.hours) && <span className="teaching-course-meta">
              {!shared && <ProgramLink program={programs[item.program]} text={text} />}
              {!shared && item.hours ? ' · ' : null}
              {item.hours ? `${item.hours} h` : null}
            </span>}
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function Teaching() {
  const { isPortuguese, t } = useTranslation();
  const text = (pt, en) => isPortuguese ? pt : en;
  const years = blocksByYear.map(({ year }) => year);
  const firstYear = Math.min(...years);
  const lastYear = Math.max(...years);

  return (
    <section className="teaching-page">
      <h1 className="page-title">{t('nav.teaching')}</h1>
      <p className="teaching-summary">
        {text(
          `${distinctCourses} disciplinas distintas entre ${firstYear} e ${lastYear}, em ${usedPrograms.length} cursos de graduação: `,
          `${distinctCourses} distinct courses between ${firstYear} and ${lastYear}, across ${usedPrograms.length} undergraduate programs: `
        )}
        {usedPrograms.map((key, index) => (
          <span key={key}>
            <a href={programs[key].url} target="_blank" rel="noreferrer">{text(programs[key].pt, programs[key].en)}</a>
            {` (${institutions[programs[key].institution].acronym})`}
            {index === usedPrograms.length - 1 ? '.' : index === usedPrograms.length - 2 ? text(' e ', ' and ') : ', '}
          </span>
        ))}
      </p>

      {blocksByYear.map(({ year, blocks }) => (
        <section key={year} className="teaching-year" aria-labelledby={`teaching-year-${year}`}>
          <h2 id={`teaching-year-${year}`} className="teaching-year-heading">{year}</h2>
          <div className="teaching-blocks">
            {blocks.map(block => (
              <TeachingBlock key={`${block.year}-${block.span}`} block={block} text={text} />
            ))}
          </div>
        </section>
      ))}
    </section>
  );
}
